import { ApplicationSearchField, ApplicationDtoStatusEnum, ApplicationSummaryDto, Role, ScreeningOrderSummaryDto } from '@inosio/payments-sdk';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  Grid,
  Stack,
  Tooltip,
  Box,
  TableContainer,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import TablePagination from '@mui/material/TablePagination';
import AppApprovedIcon from '../../icons/AppApprovedIcon.svg';
import AppDeclinedIcon from '../../icons/AppDeclinedIcon.svg';
import AppReviewedIcon from '../../icons/AppReviewedIcon.svg';
import ReviewOtpIcon from '../../icons/ReviewOtpIcon.svg';
import ScreeningSpyIcon from '../../icons/screening_spy1.svg';
import ResidentPortalIcon from '../../icons/ResidentPortal.svg';
import CircularProgress from '@mui/material/CircularProgress';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import BadRequestIcon from '../../icons/Badrequesterror.svg';
import Error500 from '../../icons/505Error.svg';
import { addDays, endOfDay, format, isValid, parseISO, startOfDay, subMonths } from 'date-fns';
import { selectApplicationsState, getFetchingApplicationsDataV2, sendCoapplicantInvitation } from '../../resui/applications/sagas/pm/applicationsSlice';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import OrderScreeningOptionsModal from './components/OrderScreeningOptionsModal';
import ScreeningOrderDownloadModal from './components/ScreeningOrderDownloadModal';
import DecisionApplicationModal from './components/DecisionApplicationModal';
import { Fragment, createRef, useEffect, useState } from 'react';
import { getAllPropertyGroupsConfigs, selectPropertyGroupDetailsState } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import RegisteredIcon from '../../icons/RegisteredIcon.svg';
import NotRegisteredIcon from '../../icons/NotRegisteredIcon.svg';
import useRegInviteConfigs, { SubjectDetailsInvitation } from '../residents/hooks/useRegInviteConfigs';
import { SubjectDetailsPassReset, usePasswordResetConfigs } from '../residents/hooks/usePasswordResetConfigs';
import RegInviteModal, { RegInviteModalStates } from '../residents/InviteResident';
import ResetPasswordModal from '../residents/ResetPassword';
import { useHistory } from 'react-router';
import SearchableTableHeader from '../shared/SearchableTableHeader';
import { IsoDateRange } from '../shared/CalendarModal.V2';
import { useQuery } from '../transactions/Transactions';
import { OrderScreeningListModal } from './components/OrderScreeningListModal';

interface DrillDownDataType {
  FullName: string;
  Type: string;
  Status: string;
  LastUpdatedDate: string;
  Uuid: string;
  IsRegistered: boolean;
  RegistrationDate: string;
  FirstName?: string;
  MiddleName?: string;
  LastName?: string;
  Email?: string;
  UserUuid?: string;
}

export default function Applications() {
  const useStyles = makeStyles({
    gridContainer: {
      background: '#FFFFFF',
      borderRadius: '24px',
    },
    dialog: {
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    buttontext: {
      fontFamily: 'sans-serif',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'small',
    },
    dialogbox: {
      borderRadius: '20',
      borderStyle: 'solid',
    },
  });
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();

  useEffect(() => {
    if (shouldRefreshAllPropertyGroupConfigs) dispatch(getAllPropertyGroupsConfigs());
  }, []);

  const optionValues = [
    { key: ApplicationSearchField.ApplicantName, value: 'Name' },
    { key: ApplicationSearchField.PropertyAddress, value: 'Property' },
  ];

  const defaultStartDate = startOfDay(addDays(subMonths(new Date(), 6), 1)).toISOString();
  const defaultEndDate = endOfDay(new Date()).toISOString();
  const [openOrderScreeningListModal, setOpenScreeningModalList] = useState<boolean>(false);
  const [screeningModalApplicantName, setScreeningModalApplicantName] = useState<string>();
  const [applicantScreeningOrders, setApplicantScreeningOrders] = useState<ScreeningOrderSummaryDto[]>([]);

  //local state
  const [searchQueryString, setSearchQueryString] = useState(''); //we use to return to this dashboard after viewing the drilldown
  const [currentPage, setCurrentPage] = useState(+(query.get('currentPage') || 0));
  const [selectedSearchDateRange, setSelectedSearchDateRange] = useState<IsoDateRange | undefined>(
    !!query.get('startDate') && !!query.get('endDate') ? [query.get('startDate'), query.get('endDate')] : undefined,
  );
  const [recordStartIndex, setRecordStartIndex] = useState(+(query.get('startIndex') || 0));
  const [rangeSize, setRangeSize] = useState(+(query.get('rangeSize') || 100));
  const [searchField, setSearchField] = useState(query.get('searchField') || '');
  const [searchValue, setSearchValue] = useState(query.get('searchVal') || '');
  const [applicationStatus, setApplicationStatus] = useState<ApplicationDtoStatusEnum>(!!query.get('status') ? (query.get('status') as ApplicationDtoStatusEnum) : ApplicationDtoStatusEnum.Submitted);
  const searchValueInputRef = createRef<HTMLInputElement>();
  const columnsSelectRef = createRef<HTMLSelectElement>();
  const [openDrop, setOpenDrop] = useState(false);

  //New application status modal: start
  const [openAppStatusModal, setOpenAppStatusModal] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [selectedApplicationUuid, setSelectedApplicationUuid] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<ApplicationSummaryDto>();
  const [openOrderScreeningOptionsModal, setOpenOrderScreeningOptionsModal] = useState(false);
  const [openScreeningOrderDownloadModal, setOpenScreeningOrderDownloadModal] = useState(false);
  const [currentApplicant, setCurrentApplicant] = useState<DrillDownDataType>();

  /* 
  start: password reset modal configs
  */
  const [openInvitationModal, setOpenInvitationModal] = useState(false);
  const [openResetPasswordModal, setOpenResetPasswordModal] = useState(false);
  const [isInvitationSpinnerModalOpen, setIsInvitationSpinnerModalOpen] = useState(false);
  const [invitationError, setInvitationError] = useState('');
  const [invitationSpinnerStatus, setInvitationSpinnerStatus] = useState<string | null>(null);
  const [activationLink, setActivationLink] = useState('');

  const [regInviteModalState, setRegInviteModalState] = useState(RegInviteModalStates.InviteToPortal);
  //selectors
  const {
    applicationsV2,
    applicationsDataV2Pages,
    isFetchingApplications,
    applicationsError,
    isChangingApplicationStatusSuccess,
    isChangingApplicationStatusFailed,
    isExportingApplicationDetails,
    isExportApplicationDetailsFailed,
    isSendingInvitationSuccess,
    CoapplicantInvitationResponseDto,
    isSendingInvitationFailed,
    sendingInvitationError,
    isCreatingScreeningOrderSuccess,
  } = useSelector(selectApplicationsState);
  const { isFetchingAllPropertyGroupConfigs, shouldRefreshAllPropertyGroupConfigs } = useSelector(selectPropertyGroupDetailsState);

  const setSearchPageIndex = (pageNumber: number) => {
    const newIndex = pageNumber * rangeSize;
    setRecordStartIndex(newIndex);
    return newIndex;
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setCurrentPage(newPageNumber);
    setSearchPageIndex(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  const handleExportAppDetails = (app: ApplicationSummaryDto) => {
    setSelectedApplication(app);
    setSelectedApplicationUuid(app.Uuid);
    history.push(`/pmui/applications/${app.Uuid}/summary`, { returnQueryString: searchQueryString });
  };

  const handleOpenOrderScreeningModal = (app: ApplicationSummaryDto) => {
    setSelectedApplication(app);
    setSelectedApplicationUuid(app.Uuid);
    setOpenOrderScreeningOptionsModal(true);
  };

  const handleOpenOrderScreeningListModal = (app: ApplicationSummaryDto) => {
    setSelectedApplication(app);
    setSelectedApplicationUuid(app.Uuid);
    setOpenScreeningModalList(true);
  };

  useEffect(() => {
    if (isChangingApplicationStatusSuccess || isCreatingScreeningOrderSuccess) {
      //refetch the apps to show that screening has been ordered for this guy.
      handleSearch2(rangeSize, recordStartIndex, applicationStatus, selectedSearchDateRange, searchField, searchValue);
    }
  }, [isChangingApplicationStatusSuccess]);

  useEffect(() => {
    setGlobalError('');
    if (isChangingApplicationStatusFailed || isExportApplicationDetailsFailed) {
      setSnackBarOpen(true);
      setGlobalError('Something went wrong. Please try again later.');
    }
  }, [isChangingApplicationStatusFailed, isExportApplicationDetailsFailed]);

  //New application status modal: end
  const getDrillDownData = (dto: ApplicationSummaryDto): DrillDownDataType[] => {
    const returnObj: DrillDownDataType[] = [];
    const primaryApplicant = dto.Applicants.find((a) => a.Uuid === dto.PrimaryApplicantUuid);
    const primaryFullName = `${primaryApplicant?.FirstName || ''}${primaryApplicant?.MiddleName ? ` ${primaryApplicant?.MiddleName}` : ''}${
      primaryApplicant?.LastName ? ` ${primaryApplicant?.LastName}` : ''
    }`;

    if (primaryApplicant)
      returnObj.push({
        FullName: primaryFullName,
        Type: 'Primary',
        Status: primaryApplicant.Status,
        LastUpdatedDate: dto.SubmittedDate ? format(new Date(dto.SubmittedDate), 'MM/dd/yyyy') : '',
        Uuid: primaryApplicant.Uuid,
        IsRegistered: true,
        RegistrationDate: primaryApplicant.UserRegistrationDate || '',
        FirstName: primaryApplicant?.FirstName,
        MiddleName: primaryApplicant.MiddleName,
        LastName: primaryApplicant.LastName,
        Email: primaryApplicant.Email,
        UserUuid: primaryApplicant.UserUuid,
      });

    const coapplicants = dto.Applicants.filter((a) => a.Uuid !== dto.PrimaryApplicantUuid);
    coapplicants.forEach((ca) => {
      const coFullName = `${ca.FirstName || ''}${ca.MiddleName ? ` ${ca.MiddleName}` : ''}${ca.LastName ? ` ${ca.LastName}` : ''}`;
      returnObj.push({
        FullName: coFullName,
        Status: ca.Status,
        Type: 'Co-Applicant',
        LastUpdatedDate: '',
        Uuid: ca.Uuid,
        IsRegistered: ca.Status.toLowerCase() !== 'invited',
        RegistrationDate: ca.UserRegistrationDate || '',
        FirstName: ca.FirstName,
        MiddleName: ca.MiddleName,
        LastName: ca.LastName,
        Email: ca.Email,
        UserUuid: ca.UserUuid,
      });
    });

    return returnObj;
  };

  const handleOpenInvitationModal = (dd: DrillDownDataType) => {
    setCurrentApplicant(dd);
    if (dd.IsRegistered) {
      setOpenResetPasswordModal(true);
    } else {
      setOpenInvitationModal(true);
    }
  };

  const handleCloseModal = () => {
    setCurrentApplicant(undefined);
    setOpenInvitationModal(false);
    setOpenResetPasswordModal(false);
  };

  const selectedApplicantDetailsInvitation: SubjectDetailsInvitation = {
    ProfileUuid: currentApplicant?.Uuid ?? '',
    Email: currentApplicant?.Email ?? '',
    FirstName: currentApplicant?.FirstName ?? '',
    MiddleName: currentApplicant?.MiddleName ?? undefined,
    LastName: currentApplicant?.LastName ?? '',
    RoleType: Role.Applicant,
  };

  const regInviteModalConfigs = useRegInviteConfigs({ handleCloseModal, subjectDetails: { ...selectedApplicantDetailsInvitation }, openModal: openInvitationModal });
  regInviteModalConfigs.handleGetInvitation = (uuid: string, email: string) => {
    setIsInvitationSpinnerModalOpen(true);
    dispatch({
      type: sendCoapplicantInvitation.type,
      payload: {
        applicationUuid: selectedApplicationUuid,
        inviteDto: {
          FirstName: '',
          LastName: '',
          Email: email,
          Uuid: uuid,
        },
      },
    });
  };

  regInviteModalConfigs.setModalState = setRegInviteModalState;
  regInviteModalConfigs.modalState = regInviteModalState;
  regInviteModalConfigs.setIsRISpinnerModalOpen = setIsInvitationSpinnerModalOpen;
  regInviteModalConfigs.isRISpinnerModalOpen = isInvitationSpinnerModalOpen;
  regInviteModalConfigs.rISpinnerStatus = invitationSpinnerStatus;
  regInviteModalConfigs.activationLink = activationLink;
  regInviteModalConfigs.setInvitationError = setInvitationError;
  regInviteModalConfigs.invitationError = invitationError;

  useEffect(() => {
    if (isSendingInvitationFailed) {
      setIsInvitationSpinnerModalOpen(false);
      setInvitationError(sendingInvitationError.message);
    }
  }, [isSendingInvitationFailed]);

  useEffect(() => {
    if (isSendingInvitationSuccess) {
      if (regInviteModalState === RegInviteModalStates.InvitationEmail) {
        setActivationLink(CoapplicantInvitationResponseDto.link);
        setRegInviteModalState(RegInviteModalStates.ActivationLink);
      }
      setInvitationSpinnerStatus('stop');
    }
  }, [isSendingInvitationSuccess]);

  const selectedApplicantDetailsPassReset: SubjectDetailsPassReset = {
    ProfileUuid: currentApplicant?.Uuid ?? '',
    Email: currentApplicant?.Email ?? '',
    CreatedAt: currentApplicant?.RegistrationDate || '',
    UserUuid: currentApplicant?.UserUuid || '',
  };

  const passwordResetModalconfigs = usePasswordResetConfigs({ handleCloseModal, subjectDetails: { ...selectedApplicantDetailsPassReset }, openModal: openResetPasswordModal });

  const handleSearch2 = (rangeSize: number, startIndex: number, status: ApplicationDtoStatusEnum, dateRange?: IsoDateRange | undefined, searchField?: string, searchValue?: string) => {
    let _searchQueryString = `?rangeSize=${rangeSize}&startIndex=${startIndex}&status=${status}&currentPage=${currentPage}`;
    if (dateRange) {
      _searchQueryString = _searchQueryString.concat(`&startDate=${dateRange[0]}&endDate=${dateRange[1]}`);
    }
    if (searchField && searchValue) {
      _searchQueryString = _searchQueryString.concat(`&searchField=${searchField}&searchVal=${searchValue}`);
    }

    //update the query string to represent what is being searched for
    setSearchQueryString(_searchQueryString);
    history.push({ pathname: '/pmui/applications', search: _searchQueryString });

    dispatch(
      getFetchingApplicationsDataV2({
        EndDate: dateRange?.[1] || defaultEndDate,
        StartDate: dateRange?.[0] || defaultStartDate,
        RangeSize: rangeSize,
        StartIndex: startIndex,
        SearchField: searchField || undefined,
        SearchValue: searchValue || undefined,
        Status: status,
      }),
    );
  };

  useEffect(() => {
    handleSearch2(rangeSize, recordStartIndex, applicationStatus, selectedSearchDateRange, searchField, searchValue);
  }, [dispatch, rangeSize, recordStartIndex, selectedSearchDateRange, searchField, searchValue, applicationStatus]);

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc( 100vh - 144px )',
          pb: '64px',
        }}
        className={classes.gridContainer}
        style={{ position: 'relative' }}
      >
        <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
          <Alert onClose={handleAlertClose} severity="error">
            {globalError}
          </Alert>
        </Snackbar>
        <Fragment>
          <Box sx={{ display: 'flex', p: 1, pb: 2, justifyContent: 'flex-start', '& .MuiTextField-root': { m: 1, width: '25ch' } }}>
            <SearchableTableHeader
              initialDateRange={selectedSearchDateRange}
              initialSearchValue={searchValue}
              searchParam={searchField ? optionValues.find((o) => o.key === searchField)?.key : optionValues[0].key}
              searchValuePlaceholder="Type Here To Search"
              dropDownOptionValues={optionValues}
              executeSearch={(searchField?: string | undefined, searchValue?: string | undefined, dateRange?: IsoDateRange | undefined) => {
                //only if both searchfield and search value are truthy do we set either
                if (!!searchField && !!searchValue) {
                  setSearchField(searchField || '');
                  setSearchValue(searchValue || '');
                }

                if (dateRange?.[0] && dateRange?.[1]) {
                  setSelectedSearchDateRange(dateRange);
                } else {
                  setSelectedSearchDateRange(undefined); //[defaultStartDate, defaultEndDate]);
                }
                setRecordStartIndex(0); //reset start index
                setCurrentPage(0); //go back to page 1
              }}
              searchValueChipCloseHandler={() => {
                setSearchField('');
                setSearchValue('');
              }}
              searchValueInputRef={searchValueInputRef} //!WTF this do?
              columnsSelectRef={columnsSelectRef} //!WTF this do?
            />
            <FormControl sx={{ pt: 0.5, pb: 2 }}>
              <Select
                onChange={(e) => {
                  const newStatus = e.target.value as ApplicationDtoStatusEnum;
                  setApplicationStatus(newStatus);
                }}
                value={applicationStatus}
                inputProps={{ 'aria-label': 'Without label' }}
                style={{ borderRadius: '24px', height: '42px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
              >
                {Object.values(ApplicationDtoStatusEnum).map((val) => (
                  <MenuItem value={val} key={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {String(applicationsError.statusCode)[0] === '4' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" pb={3} mt={3}>
                  <img src={BadRequestIcon} alt="400" />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {applicationsError.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page not found.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {applicationsError.message.length === 0 ? '' : applicationsError.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : String(applicationsError.statusCode)[0] === '5' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
                  <img src={Error500} alt="500" style={{ height: '35vh' }} />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {applicationsError.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page unavailable.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {applicationsError.message.length === 0 ? '' : applicationsError.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : isFetchingApplications || isFetchingAllPropertyGroupConfigs ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                height: '1100px',
              }}
            >
              <SpinnerWithNoConfirmation />
            </Stack>
          ) : (
            <TableContainer style={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell id="subTitle">Name</TableCell>
                    <TableCell id="subTitle">Property Name</TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Status
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Submitted Date
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Move In Date
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Applicant Details
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ left: '28px' }}>
                  {applicationsDataV2Pages?.[currentPage]?.Rows?.length ? (
                    applicationsDataV2Pages?.[currentPage]?.Rows?.map((applicationSummaryDto) => {
                      const thisDrillDownData = getDrillDownData(applicationSummaryDto);
                      const primaryApplicant = applicationSummaryDto.Applicants.find((a) => a.Uuid === applicationSummaryDto.PrimaryApplicantUuid);
                      const primaryApplicantDisplayName = `${primaryApplicant?.FirstName || ''}${primaryApplicant?.MiddleName ? ` ${primaryApplicant?.MiddleName}` : ''}${
                        primaryApplicant?.LastName ? ` ${primaryApplicant?.LastName}` : ''
                      }`;

                      let desiredMoveInDate = '';
                      if (applicationSummaryDto?.DesiredMoveInDate) {
                        const parsedDate = parseISO(applicationSummaryDto?.DesiredMoveInDate);
                        const isValidDate = isValid(parsedDate);
                        if (isValidDate) {
                          desiredMoveInDate = format(new Date(applicationSummaryDto.DesiredMoveInDate + 'T00:00:00'), 'MM/dd/yyyy');
                        }
                      }

                      return (
                        <Fragment key={applicationSummaryDto.Uuid}>
                          <TableRow
                            sx={{
                              '& .MuiTableCell-root': {
                                borderBottom: 'none',
                              },
                            }}
                          >
                            <TableCell id="content">{primaryApplicantDisplayName || ''}</TableCell>
                            <TableCell id="content">{applicationSummaryDto?.PropertyAddress || ''}</TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Tooltip
                                  title="Update Application Status"
                                  placement="right"
                                  onClick={(e) => {
                                    setSelectedApplicationUuid(applicationSummaryDto.Uuid);
                                    setSelectedApplication(applicationSummaryDto);
                                    setOpenAppStatusModal(true);
                                  }}
                                >
                                  <img
                                    src={
                                      applicationSummaryDto.Status === ApplicationDtoStatusEnum.Submitted
                                        ? AppReviewedIcon
                                        : applicationSummaryDto.Status === ApplicationDtoStatusEnum.Approved
                                        ? AppApprovedIcon
                                        : applicationSummaryDto.Status === ApplicationDtoStatusEnum.Rejected
                                        ? AppDeclinedIcon
                                        : ''
                                    }
                                    alt=""
                                    style={{ cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                {applicationSummaryDto?.SubmittedDate ? format(new Date(applicationSummaryDto.SubmittedDate), 'MM/dd/yyyy') : ''}
                              </Stack>
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                {desiredMoveInDate}
                              </Stack>
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                {isExportingApplicationDetails && selectedApplicationUuid === applicationSummaryDto.Uuid ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} width={34} height={34}>
                                    <CircularProgress size={14} />
                                  </Box>
                                ) : applicationSummaryDto.Status === ApplicationDtoStatusEnum.Incomplete ? (
                                  <></>
                                ) : (
                                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                                    <Tooltip title="View Application Details" placement="top" onClick={(e) => {}}>
                                      <Box display="flex" justifyContent="center" alignItems="center" width={34} height={34}>
                                        <img width={13} height={14} src={ReviewOtpIcon} alt="export" style={{ cursor: 'pointer' }} onClick={() => handleExportAppDetails(applicationSummaryDto)} />
                                      </Box>
                                    </Tooltip>
                                  </Stack>
                                )}
                                {selectedApplicationUuid === applicationSummaryDto.Uuid && openDrop ? (
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() => {
                                      setSelectedApplicationUuid(applicationSummaryDto.Uuid);
                                      setOpenDrop(false);
                                    }}
                                  >
                                    <KeyboardArrowUpIcon />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() => {
                                      setSelectedApplicationUuid(applicationSummaryDto.Uuid);
                                      setOpenDrop(true);
                                    }}
                                  >
                                    <KeyboardArrowDownIcon />
                                  </IconButton>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                              <Collapse in={applicationSummaryDto.Uuid === selectedApplicationUuid && openDrop} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1 }}>
                                  <Table size="small" aria-label="purchases">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell id="subTitle">Name</TableCell>
                                        <TableCell id="subTitle">Type</TableCell>
                                        <TableCell id="subTitle" align="center">
                                          Status
                                        </TableCell>
                                        <TableCell id="subTitle" align="center">
                                          Last Updated
                                        </TableCell>
                                        <TableCell id="subTitle" align="center">
                                          Registration
                                        </TableCell>
                                        <TableCell id="subTitle" align="center">
                                          Portal
                                        </TableCell>
                                        <TableCell id="subTitle" align="center">
                                          Screening
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {thisDrillDownData.map((dd) => {
                                        const screeningOrderUuid = applicationSummaryDto.ScreeningOrders?.find((so) => so.ApplicantUuid === dd.Uuid)?.ScreeningOrderUuid;
                                        const applicantScreeningOrders = applicationSummaryDto.ScreeningOrders.filter((so) => so.ApplicantUuid === dd.Uuid);
                                        return (
                                          <TableRow
                                            key={dd.Uuid}
                                            sx={{
                                              '& .MuiTableCell-root': {
                                                borderBottom: 'none',
                                              },
                                            }}
                                          >
                                            <TableCell id="content">{dd.FullName}</TableCell>
                                            <TableCell id="content">{dd.Type}</TableCell>
                                            <TableCell id="content" align="center" style={{ textTransform: 'capitalize' }}>
                                              {dd.Status.toLowerCase()}
                                            </TableCell>
                                            <TableCell id="content" align="center">
                                              {dd.LastUpdatedDate}
                                            </TableCell>
                                            <TableCell id="content" align="center">
                                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                                <Tooltip
                                                  title={dd.IsRegistered ? 'Reset Password' : 'Invite to Portal'}
                                                  placement="right"
                                                  onClick={() => {
                                                    handleOpenInvitationModal(dd);
                                                  }}
                                                >
                                                  <Box display="flex" justifyContent="center" alignItems="center" width={34} height={34}>
                                                    <img
                                                      src={dd.IsRegistered ? RegisteredIcon : NotRegisteredIcon}
                                                      alt=""
                                                      style={{ cursor: 'pointer' }}
                                                      className={dd.IsRegistered ? 'registered' : 'not-registered'}
                                                      data-e2e={dd?.Uuid}
                                                    />
                                                  </Box>
                                                </Tooltip>
                                              </Stack>
                                            </TableCell>
                                            <TableCell id="content" align="center">
                                              {dd.IsRegistered && (
                                                <Tooltip title="Login to applicant portal to assist and perform other functions." placement="right">
                                                  {/* this will log pm into applicant portal */}
                                                  <img
                                                    id="new-tab"
                                                    src={ResidentPortalIcon}
                                                    alt=""
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                      window.open(
                                                        `/login?logAsEntityUuid=${dd.Uuid}&logAsUserUuid=${dd.UserUuid}&logAsDestination=/resui/applications/${applicationSummaryDto.Uuid}/reviewsubmit`,
                                                        '_blank',
                                                      );
                                                    }}
                                                    className="logas-res-btn"
                                                  />
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                                <Tooltip title="Order Screening" placement="top" onClick={(e) => {}}>
                                                  <Box display="flex" justifyContent="center" alignItems="center" width={34} height={34}>
                                                    <img
                                                      src={ScreeningSpyIcon}
                                                      alt="Order Screening"
                                                      style={{ cursor: 'pointer' }}
                                                      onClick={() => handleOpenOrderScreeningModal(applicationSummaryDto)}
                                                    />
                                                  </Box>
                                                </Tooltip>
                                                {screeningOrderUuid && (
                                                  <Tooltip title="View Screening" placement="top" onClick={(e) => {}}>
                                                    <Box display="flex" justifyContent="center" alignItems="center" width={34} height={34}>
                                                      <img
                                                        width={13}
                                                        height={14}
                                                        src={ReviewOtpIcon}
                                                        alt="details"
                                                        style={{ cursor: 'pointer', visibility: applicationSummaryDto.ScreeningOrders?.length > 0 ? 'visible' : 'hidden' }}
                                                        onClick={() => {
                                                          if (applicantScreeningOrders.length > 1) {
                                                            setApplicantScreeningOrders(applicantScreeningOrders);
                                                            setScreeningModalApplicantName(dd.FullName);
                                                            handleOpenOrderScreeningListModal(applicationSummaryDto);
                                                          } else {
                                                            history.push(`/pmui/applications/${selectedApplicationUuid}/screening/${screeningOrderUuid}`, {
                                                              returnQueryString: searchQueryString,
                                                              returnPage: 'Applications', //We opened summary from the applications page, so return to this page as well
                                                            });
                                                          }
                                                        }}
                                                      />
                                                    </Box>
                                                  </Tooltip>
                                                )}
                                              </Stack>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 50, 100, 200]}
            count={applicationsV2.TotalCount}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rangeSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ position: 'absolute', bottom: 10, right: 10 }}
          />
        </Fragment>
      </Card>

      {openOrderScreeningOptionsModal && (
        <OrderScreeningOptionsModal
          open={openOrderScreeningOptionsModal}
          onClose={() => {
            setOpenOrderScreeningOptionsModal(false);
            //refetch the apps to show that screening has been ordered for this guy.
            handleSearch2(rangeSize, recordStartIndex, applicationStatus, selectedSearchDateRange, searchField, searchValue);
          }}
          application={selectedApplication!}
        />
      )}
      {openScreeningOrderDownloadModal && (
        <ScreeningOrderDownloadModal
          open={openScreeningOrderDownloadModal}
          onClose={() => {
            setOpenScreeningOrderDownloadModal(false);
          }}
          application={selectedApplication!}
        />
      )}
      <Box id="dialogs">
        {openInvitationModal && !!currentApplicant ? <RegInviteModal {...regInviteModalConfigs} /> : <></>}
        {openResetPasswordModal && !!currentApplicant ? <ResetPasswordModal {...passwordResetModalconfigs} /> : <></>}
      </Box>

      {selectedApplication && (
        <DecisionApplicationModal
          open={openAppStatusModal}
          propertyGroupUuid={selectedApplication?.OrganizationUuid || ''}
          onClose={() => setOpenAppStatusModal(false)}
          application={selectedApplication}
        />
      )}

      {openOrderScreeningListModal && selectedApplication && (
        <OrderScreeningListModal
          applicantScreeningOrders={applicantScreeningOrders}
          applicantName={screeningModalApplicantName}
          appDetailsSummaryState={{
            returnQueryString: searchQueryString,
            returnPage: 'Applications',
          }}
          open={openOrderScreeningListModal}
          onClose={() => {
            setOpenScreeningModalList(false);
          }}
          applicationUuid={selectedApplication.Uuid}
        />
      )}
    </Grid>
  );
}
